/**
 * Seuils du regroupement local — LE fichier de calibration.
 *
 * Tous les seuils spatiaux sont RELATIFS à la diagonale de l'emprise du plan
 * entier (fond de plan compris), notée D : un plan d'ouvrage n'a pas d'unité
 * calibrée, seule sa taille relative est fiable.
 *
 * Comment calibrer sur un vrai plan terrain :
 * 1. ouvrir le plan, lancer l'analyse, noter les regroupements ratés ;
 * 2. groupes trop ÉCLATÉS (un désordre coupé en deux) → augmenter
 *    LINK_SIZE_FACTOR (ou NEAR_GAP_FLOOR_RATIO pour les petits traits) ;
 *    groupes trop GLOUTONS (deux foyers fusionnés) → les diminuer, ou baisser
 *    NEAR_GAP_MAX_RATIO (plafond des grands aplats) ;
 * 3. textes/photos orphelins à tort → augmenter ATTACH_SIZE_FACTOR (ou
 *    ATTACH_FLOOR_RATIO pour les petits tracés) ; photos mal visées →
 *    ARROW_CONE_HALF_ANGLE_DEG / ARROW_REACH_RATIO ;
 * 4. un groupe qui s'étend de proche en proche sur tout le plan → diminuer
 *    MAX_GROUP_SPAN_RATIO.
 * Les valeurs ci-dessous sont des points de départ raisonnés, PAS calibrés.
 */

/**
 * L'écart de liaison entre deux tracés est ADAPTATIF : proportionnel à la
 * taille des tracés eux-mêmes (deux grandes zones peuvent être liées de plus
 * loin que deux petits traits), borné par un plancher et un plafond relatifs
 * au plan. Deux foyers petits et distants ne se lient donc JAMAIS, même sur un
 * très grand plan — c'est le grain « observation » (≠ constatation).
 *
 *   écart max = clamp(LINK_SIZE_FACTOR × max(diag(a), diag(b)),
 *                     NEAR_GAP_FLOOR_RATIO × D, NEAR_GAP_MAX_RATIO × D)
 */
export const LINK_SIZE_FACTOR = 0.75;
export const NEAR_GAP_FLOOR_RATIO = 0.002;
export const NEAR_GAP_MAX_RATIO = 0.015;

/**
 * Même désordre redessiné bien plus tard (> ce seuil) = probablement une
 * nouvelle observation : l'écart de liaison est multiplié par
 * FAR_TIME_GAP_DAMP. Sans effet si l'un des deux n'est pas horodaté.
 */
export const SAME_DEFECT_FAR_TIME_MS = 10 * 60 * 1000;
export const FAR_TIME_GAP_DAMP = 0.5;

/**
 * Écart de `createdAt` max (ms) pour considérer deux tracés comme un même
 * geste terrain. Des traits posés d'affilée au même endroit = un même désordre.
 */
export const TEMPORAL_GAP_MS = 60 * 1000;

/**
 * Multiplicateur d'écart spatial accordé aux tracés proches DANS LE TEMPS
 * mais de désordres différents (même catégorie de légende, ou même calque
 * quand le désordre est inconnu).
 */
export const TEMPORAL_SPATIAL_FACTOR = 3;

/**
 * Le rayon de rattachement d'un texte ou d'une photo est lui aussi ADAPTATIF : il
 * suit la taille du groupe de tracés CIBLE, borné par un plancher et un
 * plafond relatifs au plan. Indispensable sur les feuilles à PLUSIEURS fonds
 * de plan (intrados, piles… côte à côte) : la diagonale du DWG y est énorme,
 * et un rayon plat aspirerait toute la vignette vers un tracé minuscule.
 *
 *   rayon = clamp(ATTACH_SIZE_FACTOR × diag(tracés du groupe),
 *                 ATTACH_FLOOR_RATIO × D, ATTACH_GAP_RATIO × D)
 */
export const ATTACH_SIZE_FACTOR = 1.5;
export const ATTACH_FLOOR_RATIO = 0.004;
export const ATTACH_GAP_RATIO = 0.02;

/**
 * Passe photos (`photo-assignment.ts`) — la visée d'un marqueur flèche est un
 * CÔNE depuis sa base : demi-angle (la flèche est courte, l'angle imprécis)
 * et portée en part de l'échelle locale (une flèche désigne quelque chose à
 * quelques longueurs de marqueur, pas à l'autre bout de la vue).
 */
export const ARROW_CONE_HALF_ANGLE_DEG = 20;
export const ARROW_REACH_RATIO = 0.06;

/**
 * Netteté spatiale : le 2e candidat doit être au moins ce multiple plus loin
 * que le 1er pour trancher sans regarder le temps. En dessous, deux
 * distances ne se départagent pas (PHOTO_TIE_FLOOR_RATIO : écart absolu, en
 * part de l'échelle, sous lequel elles sont « égales » quoi qu'il arrive).
 */
export const PHOTO_CLARITY_RATIO = 2;
export const PHOTO_TIE_FLOOR_RATIO = 0.003;

/**
 * Pénalité de distance d'une cible déjà bien photographiée (≥
 * MANY_MARKERS_COUNT, cf. confidence.ts) dans le classement des candidats :
 * une observation porte 1 à 2 photos, un paquet de photos couvre plusieurs
 * observations voisines. Pénalité douce, jamais une exclusion.
 */
export const PHOTO_SATURATION_PENALTY = 1.5;

/**
 * Fusion des photos autonomes ENTRE ELLES (vues d'ensemble) : chaque prise
 * de vue est une observation, on ne fusionne que des marqueurs quasi collés
 * — jamais « la même feuille ».
 */
export const OVERVIEW_CLUSTER_GAP_RATIO = 0.004;

/**
 * Notes hors plan (`off-plan-notes.ts`) : deux textes forment une même LIGNE
 * s'ils se recouvrent verticalement d'au moins cette part de la plus petite
 * hauteur et si l'écart horizontal ne dépasse pas ce multiple de la hauteur.
 */
export const NOTE_ROW_OVERLAP_RATIO = 0.5;
export const NOTE_ROW_GAP_FACTOR = 1.5;

/**
 * Diagonale max d'un groupe (en part de D). Garde-fou contre l'extension de
 * proche en proche : sans lui, une fissure retouchée le long d'une poutre
 * pourrait agréger tout le plan.
 */
export const MAX_GROUP_SPAN_RATIO = 0.15;

/**
 * Tolérance d'« égalité » entre deux distances de rattachement (part relative
 * du meilleur écart) : en dessous, le départage se fait sur la proximité
 * temporelle plutôt que spatiale.
 */
export const ATTACH_TIE_TOLERANCE = 0.05;
